import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useNavContext } from "../context/NavContext";
import Nav from "./Nav";
import Spinner from "./Spinner";
import Paginador from "./Paginador";
import CalculadorForm from "./CalculadorForm";
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  setDoc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import Swal from "sweetalert2";
import { useLocation } from "react-router-dom";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia"; 

const styleForm = {
  content: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    padding: 0,
    fontFamily: "Roboto, sans-serif",
    "::WebkitScrollbar": {
      width: "0px",
      background: "transparent",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  overlay: {
    zIndex: 101,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
  },
};

const Calculador = () => {
  const location = useLocation();
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [gastos, setGastos] = useState([]);
  const [loadingGastos, setLoadingGastos] = useState(true);
  const [selectedGastos, setSelectedGastos] = useState([]);
  const [selectedGasto, setSelectedGasto] = useState(null);
  const [config, setConfig] = useState(null);
  const {
    setSearchTerm,
    proximasAVencer,
    proximosEventos,
    searchTerm,
    itemsPerPage = 25,
    currentPage = 1,
    handlePageChange,
    handleItemsPerPageChange,
  } = useNavContext();
  const { formatDate } = useFunctionsPrevia();

  // Limpia el buscador al cambiar de ruta
  useEffect(() => {
    setSearchTerm("");
  }, [location.pathname, setSearchTerm]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "gastos"),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setGastos(data);
        setLoadingGastos(false);
      },
      (error) => {
        console.error("Error al obtener gastos en tiempo real:", error);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    async function leerConfig() {
      const docRef = doc(db, "config", "numeracion");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setConfig(docSnap.data());
      } else {
        const defaultConfig = {
          prefijo: "IC-",
          siguienteNumero: 4511,
          modoNumero: "auto",
        };
        await setDoc(docRef, defaultConfig);
        setConfig(defaultConfig);
      }
    }
    leerConfig();
  }, []);

  const handleRowClick = (gasto) => {
    setSelectedGasto(gasto);
    setMostrarFormulario(true);
  };

  const handleSelectGasto = (gastoId) => {
    setSelectedGastos((prevSelected) => {
      if (prevSelected.includes(gastoId)) {
        return prevSelected.filter((id) => id !== gastoId);
      } else {
        return [...prevSelected, gastoId];
      }
    });
  };

  const handleSelectAllGastos = (e) => {
    if (e.target.checked) {
      const allIds = gastos.map((g) => g.id);
      setSelectedGastos(allIds);
    } else {
      setSelectedGastos([]);
    }
  };

  const openModal = () => {
    setSelectedGasto(null);
    setMostrarFormulario(true);
  };

  const closeModal = () => {
    setMostrarFormulario(false);
  };

  const fetchGastos = async () => {
    setLoadingGastos(true);
    try {
      const querySnapshot = await getDocs(collection(db, "gastos"));
      const data = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() });
      });
      setGastos(data);
    } catch (error) {
      console.error("Error al obtener gastos:", error);
    }
    setLoadingGastos(false);
  };

  useEffect(() => {
    fetchGastos();
  }, []);

  const handleDeleteSelected = async () => {
    if (selectedGastos.length === 0) return;

    Swal.fire({
      title: "¿Seguro que deseas eliminar?",
      text: "No podrás revertir esta acción",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      scrollbarPadding: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          for (const gastoId of selectedGastos) {
            const docRef = doc(db, "gastos", gastoId);
            await deleteDoc(docRef);
          }
          await fetchGastos();
          setSelectedGastos([]);
          Swal.fire({
            title: "Eliminados",
            text: "Los gastos se han eliminado",
            icon: "success",
            timer: 2000,
            scrollbarPadding: false,
          });
        } catch (error) {
          console.error("Error eliminando documentos:", error);
          Swal.fire({
            title: "Error",
            text: "No se pudo eliminar",
            icon: "error",
            scrollbarPadding: false,
          });
        }
      }
    });
  };

  // Filtrar gastos según el searchTerm
  const filteredGastos = gastos.filter((gasto) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (gasto.nombre && gasto.nombre.toLowerCase().includes(term)) ||
      (gasto.cliente && gasto.cliente.toLowerCase().includes(term)) ||
      (gasto.numero && gasto.numero.toString().toLowerCase().includes(term))
    );
  });

  return (
    <>
      <Nav
        handleSearch={setSearchTerm}
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <div className="task-filters">
        <div className="header-actions-proyectos">
          <button className="action-button-proyectos">
            <i className="bi bi-funnel-fill"></i> Todos
            <i className="bi bi-chevron-down" style={{ fontSize: "12px" }}></i>
          </button>
          <div className="buscador-pedidos">
            <i className="bi bi-search"></i>
            <input
              type="text"
              className="buscador-input"
              placeholder="Buscar gastos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="task-filters-right">
          <div className="delete-button-container">
            <button
              type="button"
              className="delete-button"
              style={{
                visibility: selectedGastos.length > 0 ? "visible" : "hidden",
              }}
              onClick={handleDeleteSelected}
            >
              Eliminar
            </button>
          </div>

          <div>
            <button onClick={openModal} className="new-task-button">
              + Nuevo
            </button>
          </div>
        </div>
      </div>

      <div className="cotizaciones-table">
        <div className="tabla-cotizaciones-wrapper">
          <div className="tabla-cotizaciones-content">
            {loadingGastos ? (
              <Spinner />
            ) : filteredGastos && filteredGastos.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={handleSelectAllGastos}
                        className="checkbox-select-all check"
                      />
                    </th>
                    <th>Nombre</th>
                    <th>No.</th>
                    <th>Razón Social</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGastos.map((gasto) => (
                    <tr key={gasto.id} onClick={() => handleRowClick(gasto)}>
                      <td
                        className="check"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedGastos.includes(gasto.id)}
                          onChange={() => handleSelectGasto(gasto.id)}
                          className="checkbox"
                        />
                      </td>
                      <td>{gasto.nombre}</td>
                      <td>{gasto.numero}</td>
                      <td>{gasto.cliente}</td>
                      <td>
                        <div className="estilos-fechas">
                          {formatDate(gasto.fechaVencimiento)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-resultados">
                <div
                  className="mensaje-sin-resultados"
                  style={{ marginTop: "30px" }}
                >
                  <p style={{ fontSize: "14px" }}>
                    No hay resultados disponibles
                  </p>
                </div>
                <div className="imagen-sin-resultados">
                  <img
                    src="/img/noSearch.svg"
                    alt="Sin resultados"
                    className="sin-resultados-imagen"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="tabla-cotizaciones-footer">
            <div className="paginador-container">
              <Paginador
                totalItems={filteredGastos?.length || 0}
                itemsPerPage={itemsPerPage || 10}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={mostrarFormulario}
        onRequestClose={closeModal}
        contentLabel="Nuevo Contador Form"
        style={styleForm}
      >
        <button onClick={closeModal} className="cerrar-button">
          <i className="bi bi-x-lg"></i>
        </button>
        <Nav
          handleSearch={setSearchTerm}
          proximasAVencer={proximasAVencer}
          proximosEventos={proximosEventos}
        />
        <CalculadorForm
          closeModal={() => setMostrarFormulario(false)}
          initialData={selectedGasto}
          config={config}
          setConfig={setConfig}
        />
      </Modal>
    </>
  );
};

export default Calculador;
